import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";
import TenantOnboardingDialog from "./tenant-onboarding/TenantOnboardingDialog.tsx";

// Mounted only while open, so each opening starts fresh (the tenant's current name).
interface TenantDialogProps {
  onClose: () => void;
  organizationId?: string;
  tenant?: { id: string; name: string };
}

/** Adding a tenant is the onboarding flow; with `tenant` this renames it. */
function TenantDialog({ onClose, organizationId, tenant }: TenantDialogProps) {
  if (!tenant) {
    return <TenantOnboardingDialog onClose={onClose} organizationId={organizationId} />;
  }
  return <RenameTenantDialog onClose={onClose} tenant={tenant} />;
}

function RenameTenantDialog({
  onClose,
  tenant,
}: {
  onClose: () => void;
  tenant: { id: string; name: string };
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(tenant.name);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: keys.tenant.all });
    queryClient.invalidateQueries({ queryKey: keys.organizationTenant.all });
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.updateTenant(id, { name }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const isPending = updateMutation.isPending;

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed || isPending) return;
    updateMutation.mutate({ id: tenant.id, name: trimmed });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rename Tenant</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          loading={isPending}
          disabled={!name.trim()}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TenantDialog;
